/**
 * Latency calibration dialog, `DIALOG.latencyCalibration` (W2-08 timing; DESIGN.md §5.2 "Latency calibration",
 * A§8): tap along with 16 clicks (after a 4-click count-in); the median error is the output-latency calibration.
 * It shifts only the visual clock, never the chart. The dialog writes `settings.audio.latency.calibrationMs`
 * itself when the user keeps the value and resolves with it (null when dismissed).
 *
 * Taps are any key press (except Escape, Tab and Enter) or a click on the tap area; the audio engine listens for
 * them during the session (AudioEngine.calibrate, W1-14). Closing the dialog cancels a running session (the
 * engine's optional AbortSignal, change request CR-W1-14-1).
 */

import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import { CALIBRATION_COUNT_IN } from '../../audio/latency';
import { Button, Dialog, Icon } from '../../ui';
import { m } from './timing.messages';
import { useSettingsValue, useText } from './timingHooks';
import s from './Timing.module.css';

/** Taps measured after the count-in (DESIGN.md §5.2: "tap along with 16 clicks"). */
export const CALIBRATION_TAPS = 16;

type Phase = 'intro' | 'running' | 'done' | 'error';

function reasonOf(err: unknown, tr: (r: { readonly key: string }) => string): string {
  const reason = (err as { reason?: unknown } | null)?.reason;
  if (typeof reason === 'object' && reason !== null && typeof (reason as { key?: unknown }).key === 'string') {
    return tr(reason as { readonly key: string });
  }
  return err instanceof Error ? err.message : String(err);
}

export function LatencyCalibrationDialog(props: DialogProps<'latencyCalibration'>) {
  const { services } = props;
  const text = useText(services);
  const settings = useSettingsValue(services);
  const [phase, setPhase] = useState<Phase>('intro');
  const [tick, setTick] = useState(-1);
  const [result, setResult] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const current = settings.audio.latency.calibrationMs;
  const detected = services.audio.detectedLatencyMs;
  const total = CALIBRATION_COUNT_IN + CALIBRATION_TAPS;

  useEffect(
    () => () => {
      abort.current?.abort();
    },
    [],
  );

  const start = async (): Promise<void> => {
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    setPhase('running');
    setTick(-1);
    setError(null);
    setResult(null);
    try {
      await services.audio.ensureContext();
      const ms = await services.audio.calibrate(
        (i) => {
          if (!ac.signal.aborted) setTick(i);
        },
        CALIBRATION_TAPS,
        ac.signal,
      );
      if (ac.signal.aborted) return;
      setResult(Math.round(ms));
      setPhase('done');
    } catch (err) {
      if (ac.signal.aborted) return;
      setError(reasonOf(err, text.tr));
      setPhase('error');
    }
  };

  const save = (value: number): void => {
    services.settings.set('audio', 'latency', { ...services.settings.value.audio.latency, calibrationMs: value });
    props.close({ calibrationMs: value });
  };

  const dismiss = (): void => {
    abort.current?.abort();
    props.close(null);
  };

  const status =
    phase !== 'running'
      ? null
      : tick < CALIBRATION_COUNT_IN - 1
        ? text.t(m.calCountIn, { n: CALIBRATION_COUNT_IN - Math.max(0, tick + 1) })
        : text.t(m.calTapping, {
            n: Math.min(CALIBRATION_TAPS, tick - CALIBRATION_COUNT_IN + 2),
            total: CALIBRATION_TAPS,
          });

  return (
    <Dialog
      title={text.t(m.calTitle)}
      description={text.t(m.calIntro)}
      open
      onClose={dismiss}
      size="md"
      footer={
        <>
          {current !== 0 && phase !== 'running' && (
            <Button
              variant="ghost"
              onClick={() => {
                save(0);
              }}
            >
              {text.t(m.calResetValue)}
            </Button>
          )}
          <Button variant="secondary" onClick={dismiss}>
            {text.t(phase === 'done' ? m.calClose : m.calCancel)}
          </Button>
          {phase === 'done' && result !== null ? (
            <Button
              variant="primary"
              icon="Check"
              onClick={() => {
                save(result);
              }}
              data-testid="timing-cal-save"
            >
              {text.t(m.calSave)}
            </Button>
          ) : (
            <Button
              variant="primary"
              icon="Timer"
              onClick={() => {
                void start();
              }}
              disabledReason={
                phase === 'running' ? { key: m.calTapping, params: { n: 0, total: CALIBRATION_TAPS } } : null
              }
              data-testid="timing-cal-start"
            >
              {text.t(phase === 'intro' ? m.calStart : m.calAgain)}
            </Button>
          )}
        </>
      }
    >
      <div className={s.calBody}>
        <p className={s.help}>
          {detected === null ? text.t(m.calDetectedUnknown) : text.t(m.calDetected, { ms: detected })}{' '}
          {text.t(m.calResultHelp, { ms: current })}
        </p>
        <p className={s.help}>{text.t(m.calHow)}</p>
        <div className={s.calTarget} data-testid="timing-cal-target" aria-live="polite">
          <div className={s.calDots} aria-hidden="true">
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={s.calDot} data-countin={i < CALIBRATION_COUNT_IN} data-done={tick >= i} />
            ))}
          </div>
          {status !== null && <span>{status}</span>}
          {phase === 'done' && result !== null && (
            <span className={s.calResult} data-testid="timing-cal-result">
              {text.t(m.calResult, { ms: result })}
            </span>
          )}
          {phase === 'error' && error !== null && (
            <span className={s.error} role="alert">
              <Icon name="CircleAlert" size={14} />
              {text.t(m.calFailed, { error })}
            </span>
          )}
        </div>
      </div>
    </Dialog>
  );
}
